import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, ArrowSquareOut, Printer, Copy } from "@phosphor-icons/react";

const BLANK = {
  code: "",
  event_label: "",
  event_date: "",
  consultation_minutes: 30,
  headline: "",
  blurb: "",
  status: "active",
  notes: "",
};

const SITE_BASE = "https://appistry.tech";

export default function AdminEventCodes() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [originalCode, setOriginalCode] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/event-codes");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load event codes");
    }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setOriginalCode(null);
    setEditing({ ...BLANK });
  };
  const startEdit = (row) => {
    setOriginalCode(row.code);
    setEditing({ ...row });
  };

  const save = async () => {
    if (!editing.code.trim()) { toast.error("Code is required"); return; }
    if (!editing.event_label.trim()) { toast.error("Event label is required"); return; }
    try {
      const payload = {
        ...editing,
        code: editing.code.trim().toLowerCase(),
        consultation_minutes: Number(editing.consultation_minutes) || 30,
      };
      if (originalCode) await api.put(`/admin/event-codes/${originalCode}`, payload);
      else await api.post("/admin/event-codes", payload);
      toast.success("Saved");
      setEditing(null);
      setOriginalCode(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete event code "${code}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/event-codes/${code}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const copyUrl = (code) => {
    const url = `${SITE_BASE}/event/${code}`;
    navigator.clipboard?.writeText(url);
    toast.success(`Copied ${url}`);
  };

  return (
    <div className="p-8 md:p-10" data-testid="admin-event-codes">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="overline mb-2">Event codes</p>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Event-special offer codes</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Mint a fresh code for each event. Each code becomes a public landing page at <span className="font-mono">/event/&lt;code&gt;</span> and can be printed onto A5 leaflets or A6 badges with a matching QR code.
          </p>
        </div>
        <button onClick={startCreate} className="btn-signal" data-testid="event-codes-create">
          <Plus size={16} weight="bold" /> New event code
        </button>
      </div>

      <div className="bg-white border border-zinc-200" data-testid="event-codes-table">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-zinc-200 text-[11px] font-mono uppercase tracking-widest text-zinc-500">
          <div className="col-span-2">Code</div>
          <div className="col-span-4">Event</div>
          <div className="col-span-1 text-center">Mins</div>
          <div className="col-span-1 text-center">Status</div>
          <div className="col-span-2">Updated</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {items.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-zinc-500 font-mono">No event codes yet — mint your first one to print event handouts.</div>
        )}
        {items.map((row) => (
          <div key={row.code} className="grid grid-cols-12 gap-3 px-5 py-4 border-b border-zinc-100 items-center hover:bg-zinc-50 transition-colors" data-testid={`event-code-row-${row.code}`}>
            <div className="col-span-2 font-mono text-sm font-bold text-zinc-950">{row.code}</div>
            <div className="col-span-4">
              <div className="text-sm text-zinc-950 font-medium">{row.event_label}</div>
              {row.event_date && <div className="text-xs text-zinc-500 mt-0.5 font-mono">{row.event_date}</div>}
            </div>
            <div className="col-span-1 text-center text-sm">{row.consultation_minutes}</div>
            <div className="col-span-1 text-center">
              <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 ${row.status === "active" ? "bg-emerald-100 text-emerald-700" : row.status === "draft" ? "bg-zinc-100 text-zinc-600" : "bg-zinc-200 text-zinc-500"}`}>{row.status}</span>
            </div>
            <div className="col-span-2 text-xs text-zinc-500 font-mono">{(row.updated_at || row.created_at || "").slice(0, 10)}</div>
            <div className="col-span-2 flex items-center justify-end gap-1.5 flex-wrap">
              <button onClick={() => copyUrl(row.code)} className="p-1.5 text-zinc-500 hover:text-zinc-950 transition-colors" title="Copy URL" data-testid={`event-code-copy-${row.code}`}>
                <Copy size={14} weight="bold" />
              </button>
              <Link to={`/event/${row.code}`} target="_blank" className="p-1.5 text-zinc-500 hover:text-zinc-950 transition-colors" title="View landing page" data-testid={`event-code-view-${row.code}`}>
                <ArrowSquareOut size={14} weight="bold" />
              </Link>
              <Link to={`/print/event-card/badge?code=${row.code}&minutes=${row.consultation_minutes}`} target="_blank" className="p-1.5 text-zinc-500 hover:text-zinc-950 transition-colors" title="Open A6 badge page (print or download PDF/PNG)" data-testid={`event-code-print-${row.code}`}>
                <Printer size={14} weight="bold" />
              </Link>
              {row.code === "hl1may" && (
                <>
                  <a href="/exports/appistry-a6-badge.pdf" download className="p-1.5 text-zinc-500 hover:text-zinc-950 transition-colors" title="Download A6 badge PDF" data-testid={`event-code-pdf-${row.code}`}>
                    PDF
                  </a>
                  <a href="/exports/appistry-a6-badge.png" download className="p-1.5 text-zinc-500 hover:text-zinc-950 transition-colors" title="Download A6 badge PNG" data-testid={`event-code-png-${row.code}`}>
                    PNG
                  </a>
                </>
              )}
              <button onClick={() => startEdit(row)} className="p-1.5 text-zinc-500 hover:text-zinc-950 transition-colors" title="Edit" data-testid={`event-code-edit-${row.code}`}>
                <PencilSimple size={14} weight="bold" />
              </button>
              <button onClick={() => remove(row.code)} className="p-1.5 text-zinc-500 hover:text-red-600 transition-colors" title="Delete" data-testid={`event-code-delete-${row.code}`}>
                <Trash size={14} weight="bold" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EventCodeEditor
          editing={editing}
          setEditing={setEditing}
          isNew={!originalCode}
          onSave={save}
          onCancel={() => { setEditing(null); setOriginalCode(null); }}
        />
      )}
    </div>
  );
}

function EventCodeEditor({ editing, setEditing, isNew, onSave, onCancel }) {
  const previewUrl = useMemo(() => editing.code ? `${SITE_BASE}/event/${editing.code.trim().toLowerCase()}` : "—", [editing.code]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="event-code-editor">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="font-semibold">{isNew ? "New event code" : `Edit "${editing.code}"`}</h2>
          <button onClick={onCancel} className="text-zinc-500 text-xl leading-none">×</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Code (URL slug)" hint="lowercase, no spaces. Becomes /event/<code>." required>
              <input
                value={editing.code}
                onChange={(e) => setEditing({ ...editing, code: e.target.value.toLowerCase().replace(/\s+/g, "") })}
                placeholder="hl1may"
                disabled={!isNew}
                className="w-full border border-zinc-200 px-3 py-2 bg-white font-mono disabled:bg-zinc-100"
                data-testid="event-code-input-code"
              />
            </Field>
            <Field label="Status">
              <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full border border-zinc-200 px-3 py-2 bg-white" data-testid="event-code-input-status">
                <option value="active">Active (publicly visible)</option>
                <option value="draft">Draft (admin only)</option>
                <option value="expired">Expired</option>
              </select>
            </Field>
          </div>

          <Field label="Event label" hint="Used in the landing-page hero. e.g. 'the May 1st networking event'." required>
            <input
              value={editing.event_label}
              onChange={(e) => setEditing({ ...editing, event_label: e.target.value })}
              placeholder="the May 1st networking event"
              className="w-full border border-zinc-200 px-3 py-2 bg-white"
              data-testid="event-code-input-label"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Event date (display only)" hint="Free-text — e.g. '1 May 2026'.">
              <input
                value={editing.event_date || ""}
                onChange={(e) => setEditing({ ...editing, event_date: e.target.value })}
                placeholder="1 May 2026"
                className="w-full border border-zinc-200 px-3 py-2 bg-white"
                data-testid="event-code-input-date"
              />
            </Field>
            <Field label="Consultation length (minutes)">
              <input
                type="number"
                min={5} max={120} step={5}
                value={editing.consultation_minutes}
                onChange={(e) => setEditing({ ...editing, consultation_minutes: e.target.value })}
                className="w-full border border-zinc-200 px-3 py-2 bg-white"
                data-testid="event-code-input-minutes"
              />
            </Field>
          </div>

          <Field label="Headline override (optional)" hint="If empty, uses 'Free <minutes>-min Practical Build consultation'.">
            <input
              value={editing.headline || ""}
              onChange={(e) => setEditing({ ...editing, headline: e.target.value })}
              placeholder="Free 30-min Practical Build consultation"
              className="w-full border border-zinc-200 px-3 py-2 bg-white"
              data-testid="event-code-input-headline"
            />
          </Field>

          <Field label="Body blurb override (optional)" hint="The paragraph below the hero headline.">
            <textarea
              rows={3}
              value={editing.blurb || ""}
              onChange={(e) => setEditing({ ...editing, blurb: e.target.value })}
              placeholder="A thank-you for stopping by. Bring one friction in your day…"
              className="w-full border border-zinc-200 px-3 py-2 bg-white"
              data-testid="event-code-input-blurb"
            />
          </Field>

          <Field label="Internal notes (admin only)">
            <textarea
              rows={2}
              value={editing.notes || ""}
              onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
              placeholder="Reminder: target ~50 attendees, follow up by 8 May."
              className="w-full border border-zinc-200 px-3 py-2 bg-white"
              data-testid="event-code-input-notes"
            />
          </Field>

          <div className="bg-zinc-50 border border-zinc-200 px-4 py-3 text-sm">
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Public URL</p>
            <p className="font-mono text-zinc-950 break-all" data-testid="event-code-preview-url">{previewUrl}</p>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onCancel} className="btn-ghost-ink">Cancel</button>
          <button onClick={onSave} className="btn-signal" data-testid="event-code-save">Save</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block text-sm">
      <span className="font-semibold text-zinc-800 mb-1 block">
        {label}{required && <span className="text-[hsl(var(--appistry-signal))]"> *</span>}
      </span>
      {children}
      {hint && <span className="text-xs text-zinc-500 mt-1 block">{hint}</span>}
    </label>
  );
}
